/**
 * Working a ticket from WhatsApp itself.
 *
 * An agent on a delivery round is not going to open a dashboard. Reacting to the message with
 * a tick is something they will actually do, and it is the only way a lot of this product's
 * users will ever close a ticket. The whole feature rests on two things being right: knowing
 * which emoji was sent, and knowing whether the person who sent it is allowed to act.
 */

export type ReactionAction =
  | 'resolve'
  | 'escalate'
  | 'assign'
  | 'priority'
  | 'label'
  | 'note'
  | 'ignore'

export interface ReactionRule {
  /** The emoji, normalised. */
  emoji: string
  action: ReactionAction
  /** What the action needs: a priority, a label name, a note. Unused by resolve. */
  value?: string
  enabled?: boolean
}

export const ACTION_LABELS: Record<ReactionAction, string> = {
  resolve: 'Close the ticket',
  escalate: 'Raise it to urgent and reopen',
  assign: 'Assign it to whoever reacted',
  priority: 'Set the priority',
  label: 'Apply a label',
  note: 'Add an internal note',
  ignore: 'Do nothing',
}

/**
 * The set a workspace starts with.
 *
 * Deliberately small and unambiguous. A long list of emoji nobody remembers is worse than
 * three that everyone does, and an accidental reaction that silently reassigns a ticket is
 * the fastest way to make a team distrust the whole feature.
 */
export const DEFAULT_REACTION_RULES: ReactionRule[] = [
  { emoji: '✅', action: 'resolve', enabled: true },
  { emoji: '🚨', action: 'escalate', enabled: true },
  { emoji: '👀', action: 'assign', enabled: true },
  { emoji: '📌', action: 'label', value: 'Follow up', enabled: true },
]

/* ---------------------------------------------------------------- normalising */

/** Skin tone modifiers. The gesture is the same whichever tone was used. */
const SKIN_TONE = /[\u{1F3FB}-\u{1F3FF}]/gu

/**
 * Reduces an emoji to something two clients will agree on.
 *
 * The same tick can arrive as U+2705, or as a base character followed by U+FE0F asking for
 * the colour presentation, and iOS and Android disagree about which they send. Comparing the
 * raw strings means a rule set up on one phone silently stops working when a colleague
 * reacts from another — which looks like the feature being broken at random.
 */
export function normaliseEmoji(raw: string): string {
  return (
    raw
      .trim()
      // Presentation selectors: cosmetic, never meaning.
      .replace(/[\uFE0E\uFE0F]/g, '')
      .replace(SKIN_TONE, '')
      // Zero-width joiners survive, because they are what distinguish a family from a person.
      .normalize('NFC')
  )
}

/** Whether a string is a single emoji rather than text someone typed. */
export function isSingleEmoji(raw: string): boolean {
  const value = normaliseEmoji(raw)
  if (!value) return false
  // A keycap or a flag is several code points; a sentence is several *graphemes*.
  const graphemes = [...value.matchAll(/\P{M}\p{M}*/gu)]
  if (graphemes.length === 0) return false
  return /\p{Extended_Pictographic}|\p{Emoji_Presentation}/u.test(value) && value.length <= 16
}

/* ---------------------------------------------------------------- deciding */

export interface ReactionContext {
  /** The emoji as WhatsApp sent it. An empty string means the reaction was taken back. */
  emoji: string
  /** Whether this came from one of the workspace's own connected numbers. */
  fromConnectedNumber: boolean
  /** The reactor's number, for a reaction sent from a team member's own phone in a group. */
  reactorPhone: string | null
  /** Numbers belonging to the team, in E.164. */
  operatorPhones: string[]
}

export interface ReactionDecision {
  action: ReactionAction | null
  rule: ReactionRule | null
  /** Why nothing happened, for the audit trail and for the settings screen's explanation. */
  reason: string | null
}

/**
 * Works out what a reaction should do.
 *
 * The authorisation check comes before the lookup, and it is the point of this function. A
 * customer reacting with a tick to their own delivery complaint must not close their own
 * ticket — they would, constantly, because a tick is how people say thank you. Only the
 * team's own numbers can drive anything.
 */
export function decideReaction(
  rules: ReactionRule[],
  context: ReactionContext,
): ReactionDecision {
  // An empty emoji is WhatsApp saying the reaction was removed. Reversing the original action
  // would be worse than ignoring it: un-closing a ticket someone tidied up is not obviously
  // what removing a tick was meant to mean.
  if (!context.emoji.trim()) {
    return { action: null, rule: null, reason: 'The reaction was removed.' }
  }

  const authorised =
    context.fromConnectedNumber ||
    (context.reactorPhone !== null && context.operatorPhones.includes(context.reactorPhone))

  if (!authorised) {
    return {
      action: null,
      rule: null,
      reason: 'Reactions from customers do not change anything.',
    }
  }

  const emoji = normaliseEmoji(context.emoji)
  const rule = rules.find((entry) => entry.enabled !== false && normaliseEmoji(entry.emoji) === emoji)

  if (!rule) {
    return { action: null, rule: null, reason: `No rule is set up for ${emoji}.` }
  }

  if (rule.action === 'ignore') {
    return { action: null, rule, reason: `${emoji} is set to do nothing.` }
  }

  return { action: rule.action, rule, reason: null }
}

/** Rules a workspace cannot save, checked before they are stored. */
export function validateReactionRules(rules: ReactionRule[]): string[] {
  const problems: string[] = []
  const seen = new Set<string>()

  for (const rule of rules) {
    const emoji = normaliseEmoji(rule.emoji)

    if (!emoji) {
      problems.push('One of the rules has no emoji.')
      continue
    }
    if (!isSingleEmoji(emoji)) {
      problems.push(`“${rule.emoji}” is not a single emoji.`)
      continue
    }
    // Two rules for one emoji means the outcome depends on row order, which nobody can see.
    if (seen.has(emoji)) {
      problems.push(`${emoji} is used more than once.`)
      continue
    }
    seen.add(emoji)

    if ((rule.action === 'label' || rule.action === 'note') && !rule.value?.trim()) {
      problems.push(`${emoji} needs a ${rule.action === 'label' ? 'label' : 'note'} to apply.`)
    }
    if (rule.action === 'priority' && !['low', 'normal', 'high', 'urgent'].includes(rule.value ?? '')) {
      problems.push(`${emoji} needs a priority to set.`)
    }
  }

  return problems
}

/** How the action reads in an activity log, once it has been applied. */
export function describeReaction(rule: ReactionRule, who: string): string {
  switch (rule.action) {
    case 'resolve':
      return `Closed by ${who} reacting ${rule.emoji}.`
    case 'escalate':
      return `Escalated by ${who} reacting ${rule.emoji}.`
    case 'assign':
      return `Assigned to ${who} by reacting ${rule.emoji}.`
    case 'priority':
      return `Priority set to ${rule.value} by ${who} reacting ${rule.emoji}.`
    case 'label':
      return `Labelled ${rule.value} by ${who} reacting ${rule.emoji}.`
    case 'note':
      return `${rule.value} — added by ${who} reacting ${rule.emoji}.`
    default:
      return `${who} reacted ${rule.emoji}.`
  }
}
